import { Layout, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import EtoileGif from "../../../assets/gif/etoile.gif";
import BroadcastHighlightIcon from "../../../assets/icons/broadcast-highlight.svg";
import BroadcastMicrophoneMuteIcon from "../../../assets/icons/broadcast-microphone-mute.svg";
import BroadcastMicrophoneIcon from "../../../assets/icons/broadcast-microphone.svg";
import ChallengeLiveIcon from "../../../assets/icons/challenge-live.svg";
import CloseIcon from "../../../assets/icons/close-with-shadow.svg";
import SwitchIcon from "../../../assets/icons/refresh.svg";
import UserIcon from "../../../assets/icons/user-white.svg";
import VerifyIcon from "../../../assets/icons/verify.svg";
import AvatarComponent from "../../../components/AvatarComponent";
import ChallengeProgressionBarComponent from "../../../components/ChallengeLive/ChallengeProgressionBarComponent";
import CreateChallengeLiveModalComponent from "../../../components/ChallengeLive/Creator/CreateChallengeLiveModalComponent";
import { LandscapeConfirmationModal } from "../../../components/LandscapeConfirmationModal";
import LiveBroadcastCommentComponent from "../../../components/LiveBroadcastCommentComponent";
import LivePublisherComponent from "../../../components/LivePublisherComponent";
import AgoraProvider from "../../../helpers/agoraProvider";
import { USER_TYPES } from "../../../helpers/constant";
import { addLiveComment, clearStreamStats, newLiveTip, updateStreamStats } from "../../../redux/lives/liveDataSlice";
import { clearLiveData, editLive, getLiveById } from "../../../redux/lives/livesSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { liveService } from "../../../services";
import { getSocket, offSocketReconnected, onSocketReconnected } from "../../../socket";
import { formatNumber, isMobile } from "../../../utls/FunctionsUtil";
import liveUtil from "../../../utls/LiveUtil";
import "./index.less";

const CreatorLiveBroadcastPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const publisherRef = useRef<any>(null);
  const isStartedRef = useRef<boolean>(false);
  const streamingTimeRef = useRef<number>();
  const dispatch = useDispatch<AppDispatch>();
  const live = useSelector((state: StoreState) => state.lives.item);
  const [token, setToken] = useState("");
  const liveComments = useSelector((state: StoreState) => state.liveStreamStats.comments.list);
  const streamStats = useSelector((state: StoreState) => state.liveStreamStats.streamStats.stats);
  const liveTip = useSelector((state: StoreState) => state.liveStreamStats.tips.tips);
  const [streamingTime, setStreamingTime] = useState<number | null>(null);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState<boolean>(false);
  const [showStarGif, setShowStarGif] = useState<boolean>(false);
  const [confirmCloseModal, setConfirmCloseModal] = useState<boolean>(false);
  const [challengeLiveModalVisible, setChallengeLiveModalVisible] = useState<boolean>(false);
  const [canSwitchCamera, setCanSwitchCamera] = useState<boolean>(false);
  const liveRef = useRef(live);

  useEffect(() => {
    if (!id) return;
    if (!live || live._id !== id) dispatch(getLiveById(id));
  }, [id]);

  useEffect(() => {
    liveRef.current = live;
  }, [live]);

  useEffect(() => {
    const registerLiveEventListeners = () => {
      const socket = getSocket();
      socket.off("user_joined", userJoined);
      socket.off("comment_added", addedComment);
      socket.off("tip_updated", tipUpdated);
      socket.off("user_leaved", userLeaved);
      socket.off("stream_challenge", handleChallengeCreatedOrDeleted);
      socket.off("rejoined", handleRejoined);

      socket.on("user_joined", userJoined);
      socket.on("comment_added", addedComment);
      socket.on("tip_updated", tipUpdated);
      socket.on("user_leaved", userLeaved);
      socket.on("stream_challenge", handleChallengeCreatedOrDeleted);
      socket.on("rejoined", handleRejoined);
    };

    const unregisterLiveEventListeners = () => {
      const socket = getSocket();
      socket.off("user_joined", userJoined);
      socket.off("comment_added", addedComment);
      socket.off("tip_updated", tipUpdated);
      socket.off("user_leaved", userLeaved);
      socket.off("stream_challenge", handleChallengeCreatedOrDeleted);
      socket.off("rejoined", handleRejoined);
    };

    const handleSocketReconnected = (event: CustomEvent) => {
      registerLiveEventListeners();
      if (isStartedRef.current && live?._id) {
        const socket = getSocket();
        socket.emit("public-stream/rejoin", { id: live._id });
      }
    };

    registerLiveEventListeners();
    onSocketReconnected(handleSocketReconnected);

    return () => {
      unregisterLiveEventListeners();
      offSocketReconnected(handleSocketReconnected);
    };
  }, []);

  useEffect(() => {
    if (!isStartedRef.current && live && live.sessionId) {
      const getToken = async (sessionId: string) => {
        await liveService.buildToken(sessionId).then((responseToken) => {
          if (responseToken && typeof responseToken === "string") {
            setToken(responseToken);
          }
        });
      };
      if (live && live?.streamingTime && live.streamingTime > 0) {
        setStreamingTime(live.streamingTime);
      }
      getToken(live.sessionId);
    }
  }, [live]);

  useEffect(() => {
    if (token) join();
  }, [token]);

  const join = async () => {
    if (!live || !token) return;
    publisherRef.current && (await publisherRef.current.publish());
  };

  const leave = async () => {
    publisherRef.current && (await publisherRef.current.leave());
    const socket = getSocket();
    socket.emit("public-stream/leave", {
      id: live?._id,
      streamingTime: streamingTimeRef.current,
      role: USER_TYPES.CREATOR,
    });
  };

  const streamingStatusChanged = async (started: boolean) => {
    if (started) {
      isStartedRef.current = true;
      const available = await publisherRef.current?.hasMultipleCameras();
      setCanSwitchCamera(!!available && isMobile());
    }
  };

  const handleDuration = (time: number) => {
    streamingTimeRef.current = time;
    setStreamingTime(time);
  };

  const userJoined = async (data: any) => {
    if (data.streamStats) await dispatch(updateStreamStats(data.streamStats));
    if (data.comments) await dispatch(addLiveComment(data.comments));
    if (data.lastStreamTip) await dispatch(newLiveTip(data.lastStreamTip));
  };

  const addedComment = async (data: any) => {
    await dispatch(addLiveComment(data.comments));
  };

  const tipUpdated = async (data: any) => {
    if (data?.tipData) {
      await dispatch(newLiveTip(data.tipData));
    }
    if (data?.totalPrice || data?.top3Tips) {
      await dispatch(
        editLive({ ...live, tipPrice: data.totalPrice, top3Tips: data.top3Tips, currentChallenge: data.currentChallenge || live?.currentChallenge })
      );
    }
  };

  const handleChallengeCreatedOrDeleted = (data: any) => {
    if (data?.currentChallenge) {
      dispatch(editLive({ ...live, currentChallenge: data.currentChallenge }));
    }
    if (data?.isDeleted) {
      dispatch(editLive({ ...live, currentChallenge: null }));
    }
  };

  const handleRejoined = (data: any) => {
    if (data?.streamStats) {
      dispatch(updateStreamStats(data.streamStats));
    }
    if (data?.comments) {
      dispatch(addLiveComment(data.comments));
    }
    if (data?.lastStreamTip) {
      dispatch(newLiveTip(data.lastStreamTip));
    }
  };

  const userLeaved = async (data: any) => {
    if (!liveRef.current || !liveRef.current._id) return;
    if (data.role === "fan" && data.streamStats && data.streamId === liveRef.current._id) {
      await dispatch(updateStreamStats(data.streamStats));
    }
  };

  const onClickSwitchCamera = async () => {
    try {
      await publisherRef.current?.switchCamera();
    } catch (error) {
      message.error(t("camera-switch-failed"));
    }
  };

  const handleMicrophoneToggle = async () => {
    if (publisherRef.current) {
      if (isMicrophoneMuted) {
        await publisherRef.current.unmute();
      } else {
        await publisherRef.current.mute();
      }
      setIsMicrophoneMuted(!isMicrophoneMuted);
    }
  };

  const handleChallengeLive = () => {
    setChallengeLiveModalVisible(true);
  };

  const closeChallengeLiveModal = () => {
    setChallengeLiveModalVisible(false);
  };

  const handleEndLive = () => {
    setConfirmCloseModal(!confirmCloseModal);
  };

  const handleConfirmeEndLive = async () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen();
    }
    publisherRef.current?.clearStreamDuration?.();
    leave();
    dispatch(clearLiveData());
    dispatch(clearStreamStats());
    navigate("/creator/profile");
  };

  const handleCancelEndLive = () => {
    setConfirmCloseModal(!confirmCloseModal);
  };

  const handleHighlightClick = async () => {
    if (!id || streamingTime === null) return;

    setShowStarGif(true);
    setTimeout(() => {
      setShowStarGif(false);
    }, 5000);

    const highlightTime = Math.max(0, streamingTime - 10);

    try {
      await liveService.createHighlight(id, highlightTime);
    } catch (error) {
      message.error(t("error-occurred"));
    }
  };

  useEffect(() => {
    return () => {
      if (isStartedRef.current) {
        leave();
      }
    };
  }, []);

  return (
    <AgoraProvider config={{ mode: "live", codec: "h264", role: "host" }}>
      <Layout>
        <Layout.Content className="content creatorLiveBroadcastPage">
          <div className="broadcast-page-wrapper">
            <div className="broadcast-main-container">
              <div className="broadcast-live-section">
                <LivePublisherComponent
                  streamingTime={streamingTime}
                  onStatusChanged={streamingStatusChanged}
                  handleDuration={handleDuration}
                  token={token}
                  ref={publisherRef}
                  landscape={true}
                />
                <div className="broadcast-header-container">
                  <div className="broadcast-header-content">
                    <div className="broadcast-header-left">
                      <AvatarComponent image={live?.creator?.avatar} size={25} />
                      <div className="broadcast-username">
                        {live?.creator?.username} {live?.creator?.verifiedEmail && <img src={VerifyIcon} alt="verify" />}
                      </div>

                      {streamingTime !== null && (
                        <div className="broadcast-streaming-time">{streamingTime && liveUtil.getDurationString(streamingTime)}</div>
                      )}
                    </div>
                  </div>
                  {live?.currentChallenge && (
                    <div className="broadcast-challenge-progression">
                      <ChallengeProgressionBarComponent challenge={live?.currentChallenge} isCreator={true} />
                    </div>
                  )}
                  <div className="header-top-right-icons">
                    <div className="icons-row">
                      {live && streamStats && (
                        <div className="broadcast-spectators-tag">
                          <img src={UserIcon} width={10} />
                          <div className="broadcast-spectators-count">{formatNumber(streamStats.members)}</div>
                        </div>
                      )}
                      {live && <img src={ChallengeLiveIcon} width={24} alt="challenge" onClick={handleChallengeLive} />}
                      {canSwitchCamera && (
                        <img src={SwitchIcon} width={22} alt="switch" onClick={onClickSwitchCamera} style={{ cursor: "pointer" }} />
                      )}
                      <img src={CloseIcon} width={22} alt="close" onClick={handleEndLive} />
                    </div>
                  </div>
                </div>

                <div className="broadcast-icons-row">
                  {showStarGif ? (
                    <div className="broadcast-highlight-gif-container" onClick={handleHighlightClick}>
                      <img src={EtoileGif} alt="broadcast-highlight-gif" className="broadcast-highlight-gif" />
                    </div>
                  ) : (
                    <img
                      src={BroadcastHighlightIcon}
                      width={50}
                      alt="broadcast-highlight"
                      onClick={handleHighlightClick}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                  <img
                    src={isMicrophoneMuted ? BroadcastMicrophoneMuteIcon : BroadcastMicrophoneIcon}
                    width={50}
                    alt="broadcast-microphone"
                    onClick={handleMicrophoneToggle}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
              <div className="broadcast-comment-section">
                <LiveBroadcastCommentComponent comments={liveComments} tipPrice={live?.tipPrice} lastTip={liveTip} top3Tips={live?.top3Tips} />
              </div>
            </div>
          </div>
        </Layout.Content>
        <LandscapeConfirmationModal
          handleConfirm={handleConfirmeEndLive}
          handleToggle={handleCancelEndLive}
          open={confirmCloseModal}
          confBtnMsg={t("modal-confirme-end-live-btn")}
          title={t("modal-end-live-msg")!}
        />
        <CreateChallengeLiveModalComponent
          streamId={live?._id}
          visible={challengeLiveModalVisible}
          onClose={closeChallengeLiveModal}
          className="broadcast-challenge-modal"
        />
      </Layout>
    </AgoraProvider>
  );
};

export default CreatorLiveBroadcastPage;
